import { Component, ViewChild, AfterViewInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location } from '@angular/common';
import { ShopifyStorefrontService } from '../../services/shopify-storefront.service';
import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
// import { getProductByHandle } from '../../services/product-queries/get-product';
import { ProductSwatchesComponent } from '../../components/product-swatches/product-swatches.component';
import { TitleWithoutColorPipe } from '../../pipes/title-without-color.pipe';
import { ColorWithoutTitlePipe } from '../../pipes/color-without-title.pipe';
import 'swiper/element';
import { register } from 'swiper/element/bundle';
import { SizeChartComponent } from '../../components/size-chart/size-chart.component';
import { getProductByHandle } from '../../services/product-queries/get-product';
import { RemoveTaaBerryPipe } from '../../pipes/remove-taa-berry.pipe';
import { ShortenSizePipe } from '../../pipes/shorten-size.pipe';
import { getCollectionProductsByHandle } from '../../services/product-queries/get-collection-products';
import { NewlineToBrPipe } from '../../pipes/newline-to-br.pipe';
// import { StyleNumFromSkuPipe } from '../../pipes/style-num-from-sku.pipe';

register();

@Component({
  selector: 'app-product-detail-page',
  imports: [
    CommonModule,
    ProductSwatchesComponent,
    TitleWithoutColorPipe,
    ColorWithoutTitlePipe,
    RouterModule,
    RemoveTaaBerryPipe,
    ShortenSizePipe,
    NewlineToBrPipe,
  ],
  templateUrl: './product-detail-page.component.html',
  styleUrls: ['./product-detail-page.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductDetailPageComponent implements AfterViewInit {
  @ViewChild('galleryThumbs', { static: false }) galleryThumbs: any;
  @ViewChild('galleryTop', { static: false }) galleryTop: any;
  @ViewChild('sizeChart', { static: false }) sizeChartDialog: any;
  @ViewChild('fullScreenImageGallery', { static: false })
  fullScreenImageGallery: any;

  sliderIndex: WritableSignal<number> = signal(0);
  // Initially, changeProduct is null.
  private changeProduct: WritableSignal<any> = signal(null);

  // The product handle comes either from navigation state or the route.
  private productHandle!: string;

  // thumbsSwiper: any;
  productCompliance: WritableSignal<string> = signal('TAA');

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private data: ShopifyStorefrontService,
    public location: Location,
    private sanitizer: DomSanitizer
  ) {
    try {
      const navigation = this.router.getCurrentNavigation();
      const navProduct = navigation?.extras?.state?.['product'];
      if (navProduct) {
        // Use passed product and record its handle.
        this.changeProduct.set(navProduct);
        this.productHandle = navProduct.handle;
        this.productCompliance.set(
          navProduct.title.includes(' TAA ') ? 'TAA' : 'BERRY'
        );
      } else {
        this.productHandle = this.route.snapshot.paramMap.get('handle')!;
        this.changeProduct.set(
          this.data.getCachedProductByHandle(this.productHandle)
        );
        const prod = this.changeProduct();
        if (prod?.title) {
          this.productCompliance.set(
            prod.title.includes(' TAA ') ? 'TAA' : 'BERRY'
          );
        }
      }
    } catch (error) {
      console.log('Error retrieving product handle:', error);
    }
  }

  fetchProduct() {
    this.productHandle = this.route.snapshot.paramMap.get('handle')!;
    this.data
      .genericQuery(getProductByHandle, { handle: this.productHandle })
      .subscribe(
        (data: any) => {
          const product = data.data.productByHandle;
          if (product) {
            this.changeProduct.set(product);
            this.productCompliance.set(
              product.title.includes(' TAA ') ? 'TAA' : 'BERRY'
            );
          } else {
            console.log('Product not found:', this.productHandle);
          }
        },
        (error: any) => {
          console.error('Error fetching product:', error);
        }
      );
  }

  fetchProducts(
    handle: string = 'taa-berry-all',
    first: number = 20,
    after: any = null
  ) {
    this.data
      .genericQuery(getCollectionProductsByHandle, { handle, first, after })
      .subscribe((data: any) => {
        const hasNextPage = data.data.collection.products.pageInfo.hasNextPage;
        const endCursor = data.data.collection.products.pageInfo.endCursor;
        if (hasNextPage) {
          this.fetchProducts(handle, first, endCursor);
        }
        // Set the products in the data service
        const existingProducts = this.data.allProducts() || [];
        const newProducts = data.data.collection.products.edges.map(
          (edge: any) => edge.node
        );
        this.data.allProducts.set([...existingProducts, ...newProducts]);
      });
  }

  get product() {
    const prod =
      this.changeProduct() ||
      this.data
        .allProducts()
        .find((prod: any) => prod.handle === this.productHandle);
    if (!prod && typeof window === 'undefined') {
      console.error('❌ SSR: Product not found for handle', this.productHandle);
    }
    return prod;
  }
  // get product() {
  //   return (
  //     this.changeProduct() ||
  //     this.data
  //       .allProducts()
  //       .find((prod: any) => prod.handle === this.productHandle)
  //   );
  // }

  get schemaMarkup(): SafeHtml | null {
    const p = this.product;
    if (!p) return null;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: p.title,
      image: p.featuredImage?.url,
      description: p.description,
      sku: p.style_name.value,
      brand: {
        '@type': 'Brand',
        name: p.vendor,
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: p.priceRange?.maxVariantPrice?.currencyCode,
        price: p.priceRange?.maxVariantPrice?.amount,
        availability: 'https://schema.org/InStock',
      },
    };

    const scriptTag = `<script type="application/ld+json">${JSON.stringify(
      schema
    )}</script>`;
    return this.sanitizer.bypassSecurityTrustHtml(scriptTag);
  }

  onProductSelected($event: any) {
    this.changeProduct.set($event);
  }

  switchProductCompliance(selector: string) {
    if (!this.product) {
      console.log('No product to switch compliance for.');
      return;
    }
    const title = this.product.title;
    const swap = selector === 'TAA' ? 'berry' : 'taa';
    const searchTitle = title.toLowerCase().replace(swap, selector);
    const product = this.data.getProductByTitle(searchTitle);
    if (product) {
      this.changeProduct.set(product);
      this.productCompliance.set(selector);
      console.log(
        'Switched product compliance to:',
        selector,
        this.changeProduct()
      );
      return;
    } else {
      console.log('Product not found:', searchTitle);
    }
  }

  selectedOptions: any = {};
  selectOption(optionName: string, value: string) {
    this.selectedOptions[optionName] = value;
  }
  changeSwiperImage(index: number) {
    this.galleryTop.nativeElement.swiper.slideTo(index);
    this.sliderIndex.set(index);
  }
  ngAfterViewInit() {
    this.galleryTop.nativeElement.swiper.on(
      'slideChange',
      this.onSlideChange.bind(this)
    );
  }

  slideChangeTimer: any;
  onSlideChange(event: any): void {
    console.log('Slide changeing');
    clearTimeout(this.slideChangeTimer);
    this.slideChangeTimer = setTimeout((event: any) => {
      console.log(
        'Slider gallery top change:',
        this.galleryTop.nativeElement.swiper.activeIndex
      );
      this.sliderIndex.set(this.galleryTop.nativeElement.swiper.activeIndex);
      console.log('Slider index:', this.sliderIndex());
    }, 10);
  }

  openSizeChart() {
    this.sizeChartDialog.nativeElement.showModal();
  }
  closeSizeChart() {
    this.sizeChartDialog.nativeElement.close();
  }
  openImageGallery(i: number) {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return;
    }

    const element = this.fullScreenImageGallery.nativeElement.querySelector(
      `[data-index="${i}"]`
    );
    this.fullScreenImageGallery.nativeElement.showModal();
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }
  closeImageGallery() {
    this.fullScreenImageGallery.nativeElement.close();
  }
}
